import { useMutation, useQuery } from '@apollo/client';
import { Empty, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ApplicantUserContactInformationInput,
  StaffApplicantProfilePersonalInformationContactContainerDocument,
  StaffApplicantProfileUpdatePersonalInformationContactDocument
} from '../../../../generated';
import { lookupCountryQuery } from './staff-components-country-query';
import { Country } from '../../../../types/country';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { ApplicantProfileContactEdit } from './applicant-profile-contact-edit';

interface ApplicantProfileContactEditContainerProps {
  applicantId: string;
}

export const ApplicantProfileContactEditContainer: React.FC<ApplicantProfileContactEditContainerProps> = (props) => {
  const navigate = useNavigate();

  const {
    data: contactData,
    loading: contactLoading,
    error: contactError
  } = useQuery(StaffApplicantProfilePersonalInformationContactContainerDocument, {
    variables: {
      applicantUserId: props?.applicantId
    }
  });

  const { data: countryData, error: countryError } = useQuery(lookupCountryQuery);

  const [applicantUserUpdate] = useMutation(StaffApplicantProfileUpdatePersonalInformationContactDocument);

  const handleSave = async (data: ApplicantUserContactInformationInput) => {
    try {
      const result = await applicantUserUpdate({
        variables: {
          input: {
            id: props?.applicantId,
            applicantUserDetails: {
              personalInformation: {
                contactInformation: {
                  address: data?.address,
                  phone: data?.phone
                }
              }
            }
          }
        }
      });
      if (result.data?.applicantUserUpdate.status.success) {
        message.success('Contact details updated');
        handleEdit();
      } else {
        message.error('Failed to update Contact details');
      }
    } catch (error) {
      message.error(`Error Updating Contact: ${JSON.stringify(error)}`);
    }
  };

  const isCountryArray = (data: unknown): data is Country[] => Array.isArray(data) && data.every((item) => 'countryName' in item && 'countryCode' in item);

  const countryDataFiltered = isCountryArray(countryData?.countries?.eventPayload?.countries) ? countryData.countries.eventPayload.countries : [];

  const handleEdit = () => {
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/\/[^/]+$/, '/view');
    navigate(newPath);
  };

  if (contactError) {
    return <Empty description="There was an error loading the data. Please try again!" />;
  }

  if (countryError) {
    return <Empty description={`There was an error loading the country data. Please try again! ${countryError}`} />;
  }

  const error = contactError || countryError;
  const isLoading = contactLoading || !countryData;
  const hasData = contactData?.applicantUser?.personalInformation?.contactInformation !== undefined;

  return (
    <ComponentQueryLoader
      error={error}
      loading={isLoading}
      hasData={hasData}
      hasDataComponent={
        <ApplicantProfileContactEdit
          contactInformation={{
            ...contactData?.applicantUser?.personalInformation?.contactInformation,
            phone: contactData?.applicantUser?.personalInformation?.contactInformation?.phone?.map((phone) => {
              return {
                id: phone?.id,
                phoneNumber: phone?.phoneNumber,
                phoneType: phone?.phoneType,
                isPrimary: phone?.phoneNumber === contactData?.applicantUser?.personalInformation?.contactInformation?.primaryPhone
              };
            })
          }}
          countryData={countryDataFiltered}
          onCancel={handleEdit}
          onSave={handleSave}
        />
      }
    />
  );
};
